import { z } from "zod";
import { isIsoDate, isValidTimeZone } from "./dates.js";
import type { EditProposal } from "./edits.js";
import type { TaskProposal } from "./tasks.js";

/** A habit or task can last up to a full day: a long study or work block is a real plan. */
export const MAX_DURATION_MINUTES = 24 * 60;

export const isoDateSchema = z.string().refine(isIsoDate, "Fecha inválida (YYYY-MM-DD)");
export const timeSchema = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, "Hora inválida (HH:mm)");
export const timeZoneSchema = z.string().refine(isValidTimeZone, "Zona horaria inválida");

export const registerSchema = z.object({
  name: z.string().trim().min(1).max(60),
  email: z.email().transform((e) => e.toLowerCase()),
  password: z.string().min(8, "Mínimo 8 caracteres").max(200),
  timezone: timeZoneSchema,
  inviteCode: z.string().optional(),
});
export type RegisterInput = z.infer<typeof registerSchema>;

export const loginSchema = z.object({
  email: z.email().transform((e) => e.toLowerCase()),
  password: z.string().min(1).max(200),
});
export type LoginInput = z.infer<typeof loginSchema>;

export const habitInputSchema = z
  .object({
    title: z.string().trim().min(1).max(80),
    emoji: z.string().max(8).nullable().default(null),
    color: z.string().regex(/^#[0-9a-fA-F]{6}$/).default("#6366f1"),
    time: timeSchema,
    durationMinutes: z.number().int().min(5).max(MAX_DURATION_MINUTES).default(30),
    daysOfWeek: z
      .array(z.number().int().min(0).max(6))
      .min(1, "Elige al menos un día")
      .transform((days) => [...new Set(days)].sort()),
    startDate: isoDateSchema,
    endDate: isoDateSchema.nullable().default(null),
    reminder: z.boolean().default(true),
  })
  .refine((h) => !h.endDate || h.endDate >= h.startDate, {
    message: "La fecha final debe ser posterior al inicio",
    path: ["endDate"],
  });
export type HabitInput = z.infer<typeof habitInputSchema>;

export const checkInStatusSchema = z.enum(["done", "skipped"]);
export type CheckInStatus = z.infer<typeof checkInStatusSchema>;

export const checkInInputSchema = z.object({
  habitId: z.string().min(1),
  date: isoDateSchema,
  /** null clears the check-in (undo). */
  status: checkInStatusSchema.nullable(),
});
export type CheckInInput = z.infer<typeof checkInInputSchema>;

export const pushSubscriptionSchema = z.object({
  endpoint: z.url(),
  keys: z.object({ p256dh: z.string().min(1), auth: z.string().min(1) }),
});

/** Shapes the API returns. */
export interface UserDto {
  id: string;
  name: string;
  email: string;
  timezone: string;
  /** False until the onboarding questionnaire is finished (or skipped). */
  onboarded: boolean;
  /** Daily pushes: summary of the day in the morning, what's left before bed. */
  morningSummary: boolean;
  eveningWrapUp: boolean;
}

export interface HabitDto extends HabitInput {
  id: string;
  goalId: string | null;
  archivedAt: string | null;
}

export interface CheckInDto {
  habitId: string;
  date: string;
  status: CheckInStatus;
}

export const goalPlanRequestSchema = z.object({
  title: z.string().trim().min(3, "Cuéntame un poco más de tu meta").max(200),
  details: z.string().trim().max(1000).nullable().default(null),
  targetDate: isoDateSchema.nullable().default(null),
  /** Free text: when the user has time, what to avoid. */
  availability: z.string().trim().max(500).nullable().default(null),
});
export type GoalPlanRequest = z.infer<typeof goalPlanRequestSchema>;

/** Up to this many steps per plan; more than a handful at once is how habits get dropped. */
/**
 * Most steps in one plan: a guard for the server, not a design limit. A pasted plan can be
 * months of packed days; the AI sends it in parts and they add up to one proposal.
 */
export const MAX_PLAN_STEPS = 300;

export const goalCreateSchema = goalPlanRequestSchema.extend({
  planSummary: z.string().max(2000).nullable().default(null),
  habits: z.array(habitInputSchema).min(1, "Elige al menos un paso").max(MAX_PLAN_STEPS),
});
export type GoalCreateInput = z.infer<typeof goalCreateSchema>;

export const replanRequestSchema = z.object({
  /** Optional: what got in the way, in the user's words. */
  note: z.string().trim().max(500).nullable().default(null),
});
export type ReplanRequest = z.infer<typeof replanRequestSchema>;

export const applyPlanSchema = z.object({
  planSummary: z.string().max(2000).nullable().default(null),
  habits: z.array(habitInputSchema).min(1, "Elige al menos un paso").max(MAX_PLAN_STEPS),
});
export type ApplyPlanInput = z.infer<typeof applyPlanSchema>;

/** A plan the AI proposed. Nothing is saved until the user accepts it. */
export interface PlanProposal {
  summary: string;
  steps: Array<HabitInput & { why: string }>;
}

export interface GoalProgress {
  /** Last 7 days up to yesterday: today isn't over yet. */
  scheduled: number;
  done: number;
  missed: number;
  /** Several recent misses: worth offering a lighter plan. */
  needsReplan: boolean;
}

export interface GoalDto {
  id: string;
  title: string;
  details: string | null;
  targetDate: string | null;
  availability: string | null;
  planSummary: string | null;
  color: string;
  archivedAt: string | null;
  habitIds: string[];
  progress: GoalProgress;
  /** Last four weeks up to today, across replans: the long view of the goal. */
  recent: { scheduled: number; done: number };
}

/** A chat message can be a whole pasted plan; this only guards the server (~25k tokens). */
export const CHAT_MAX_CHARS = 100_000;

export const chatInputSchema = z.object({
  text: z
    .string()
    .trim()
    .min(1)
    .max(CHAT_MAX_CHARS, `El mensaje es muy largo: el máximo son ${CHAT_MAX_CHARS.toLocaleString("es")} caracteres`),
});

/** A chat turn the server is still working on (HTTP 202), polled at `/api/chat/turns/:id`. */
export interface ChatTurnPending {
  pending: string;
  /** The user's message, to show again when the chat is reopened mid-turn. */
  text: string;
  startedAt: string;
  /** Which try at the AI: 2 or more means the first model was busy and another is answering. */
  attempt: number;
  /** Which part of a long plan the AI is writing (1 until a plan needs more than one). */
  part: number;
}

export interface ChatMessageDto {
  id: string;
  role: "user" | "assistant";
  text: string;
  /** A plan the assistant proposed in this message, waiting for the user. */
  proposal: (PlanProposal & { goalTitle: string }) | null;
  /** Or one-off tasks for the coming days. At most one of the two is set. */
  taskProposal: TaskProposal | null;
  /** Or changes to habits and tasks already on the calendar. */
  editProposal: EditProposal | null;
  proposalStatus: "pending" | "accepted" | null;
  createdAt: string;
}

export const acceptChatPlanSchema = z.object({
  habits: z.array(habitInputSchema).min(1, "Elige al menos un paso").max(MAX_PLAN_STEPS),
});
